import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Stack,
  Grid,
  Paper,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const DOCUMENTATION_TYPES = [
  "user",
  "technical",
  "architecture",
  "troubleshooting",
];

const DocumentationForm = ({ data, onChange }) => {
  const [pageReferences, setPageReferences] = useState(
    data?.pageReference || []
  );
  const [newPageName, setNewPageName] = useState("");
  const [newPagePriority, setNewPagePriority] = useState(1);
  const [tags, setTags] = useState(data?.tags || []);
  const [newTag, setNewTag] = useState("");

  // Update form when data prop changes
  useEffect(() => {
    if (data?.pageReference) {
      setPageReferences(data.pageReference);
    }
    if (data?.tags) {
      setTags(data.tags);
    }
  }, [data?.key]); // Reset when switching docs

  const handleFieldChange = (field, value) => {
    onChange({
      ...data,
      [field]: value,
    });
  };

  const handleAddPageReference = () => {
    if (!newPageName.trim()) {
      alert("Please enter a page name");
      return;
    }

    const newRef = {
      pageName: newPageName.trim(),
      priority: newPagePriority,
    };

    const updated = [...pageReferences, newRef];
    setPageReferences(updated);
    onChange({
      ...data,
      pageReference: updated,
    });

    setNewPageName("");
    setNewPagePriority(1);
  };

  const handleRemovePageReference = (index) => {
    const updated = pageReferences.filter((_, i) => i !== index);
    setPageReferences(updated);
    onChange({
      ...data,
      pageReference: updated,
    });
  };

  const handleAddTag = () => {
    if (!newTag.trim()) {
      alert("Please enter a tag");
      return;
    }

    const updated = [...tags, newTag.trim()];
    setTags(updated);
    onChange({
      ...data,
      tags: updated,
    });

    setNewTag("");
  };

  const handleRemoveTag = (indexToRemove) => {
    const updated = tags.filter((_, i) => i !== indexToRemove);
    setTags(updated);
    onChange({
      ...data,
      tags: updated,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Core Fields */}
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          Document Information
        </Typography>

        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
            sm={6}
          >
            <TextField
              fullWidth
              label="Document Key"
              value={data?.key || ""}
              onChange={(e) => handleFieldChange("key", e.target.value)}
              placeholder="e.g., user-guide-invoicing"
              helperText="Unique identifier, use lowercase with hyphens"
              required
              disabled={!!data?.id} // Can't change key of existing doc
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
          >
            <FormControl
              fullWidth
              required
            >
              <InputLabel>Type</InputLabel>
              <Select
                value={data?.type || ""}
                label="Type"
                onChange={(e) => handleFieldChange("type", e.target.value)}
              >
                {DOCUMENTATION_TYPES.map((type) => (
                  <MenuItem
                    key={type}
                    value={type}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Category for organization</FormHelperText>
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              fullWidth
              label="Title"
              value={data?.title || ""}
              onChange={(e) => handleFieldChange("title", e.target.value)}
              placeholder="Document title"
              required
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              fullWidth
              label="Description"
              value={data?.description || ""}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              placeholder="Brief description of the document"
              multiline
              rows={2}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
          >
            <TextField
              fullWidth
              label="Language"
              value={data?.language || "en"}
              onChange={(e) => handleFieldChange("language", e.target.value)}
              select
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="fr">French</MenuItem>
              <MenuItem value="de">German</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tags */}
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          Tags
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            label="Add tag"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleAddTag();
              }
            }}
            placeholder="e.g., billing"
          />
          <Button
            variant="contained"
            onClick={handleAddTag}
            size="small"
          >
            Add
          </Button>
        </Stack>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {tags.map((tag, idx) => (
            <Chip
              key={idx}
              label={tag}
              onDelete={() => handleRemoveTag(idx)}
            />
          ))}
        </Box>
      </Paper>

      {/* Page References - For PageHeader Icon */}
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          Page References (for Help Icon)
        </Typography>

        <Typography
          variant="caption"
          display="block"
          sx={{ mb: 2, color: "textSecondary" }}
        >
          Assign this documentation to pages. The help icon will appear on those
          pages, sorted by priority (lower number = higher priority).
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            label="Page Name"
            value={newPageName}
            onChange={(e) => setNewPageName(e.target.value)}
            placeholder="e.g., invoices"
          />
          <TextField
            type="number"
            size="small"
            label="Priority"
            value={newPagePriority}
            onChange={(e) => setNewPagePriority(parseInt(e.target.value))}
            inputProps={{ min: 1, max: 100 }}
            sx={{ width: 100 }}
          />
          <Button
            variant="contained"
            onClick={handleAddPageReference}
            size="small"
          >
            Add
          </Button>
        </Stack>

        {pageReferences.length > 0 ? (
          <Stack spacing={1}>
            {pageReferences.map((ref, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold" }}
                  >
                    {ref.pageName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    Priority: {ref.priority}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemovePageReference(idx)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography
            variant="caption"
            color="textSecondary"
          >
            No page references yet. Add one to make this documentation
            available.
          </Typography>
        )}
      </Paper>

      {/* Advanced */}
      {data?.id && (
        <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, fontWeight: "bold" }}
          >
            Document Info
          </Typography>
          <Typography
            variant="caption"
            display="block"
          >
            Created by: {data?.creatorName || "Unknown"}
          </Typography>
          <Typography
            variant="caption"
            display="block"
          >
            Reading time: {data?.readingTimeMinutes} min
          </Typography>
          <Typography
            variant="caption"
            display="block"
          >
            Updated: {new Date(data?.updatedAt).toLocaleDateString()}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DocumentationForm;
